import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Music, 
  Plus, 
  Upload, 
  Trash2, 
  Edit, 
  X, 
  Save, 
  Play, 
  Pause,
  Clock,
  User,
  FolderOpen,
  FileAudio,
  Image as ImageIcon,
  BarChart2,
  RefreshCw,
  FileJson,
  ExternalLink,
  Files,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'meditation', label: 'Meditation & Mindfulness' },
  { value: 'motivation', label: 'Motivational' },
  { value: 'relaxation', label: 'Relaxation & Sleep' },
  { value: 'podcast', label: 'Podcasts' },
  { value: 'music', label: 'Background Music' },
  { value: 'affirmation', label: 'Affirmations' },
  { value: 'nature', label: 'Nature Sounds' },
  { value: 'other', label: 'Other' },
];

export default function AdminAudio() {
  const { currentAdminTheme } = useTheme();
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [generatingCatalog, setGeneratingCatalog] = useState(false);
  const [catalogUrl, setCatalogUrl] = useState(null);
  
  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('other');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkResults, setBulkResults] = useState(null);
  
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    artist: '',
    duration: '',
    file: null,
    thumbnail: null
  });

  useEffect(() => {
    fetchAudio();
  }, []);

  const fetchAudio = async () => {
    try {
      const response = await axios.get(`${API}/admin/audio`, { withCredentials: true });
      setAudioFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching audio:', error);
      toast.error('Failed to load audio files');
    } finally {
      setLoading(false);
    }
  };

  const generateCatalog = async () => {
    setGeneratingCatalog(true);
    try {
      const response = await axios.post(`${API}/admin/audio/generate-catalog`, {}, { withCredentials: true });
      setCatalogUrl(`${BACKEND_URL}${response.data.catalog_url}`);
      toast.success(`Catalog generated with ${response.data.total_files} audio files!`);
    } catch (error) {
      toast.error('Failed to generate catalog');
    } finally {
      setGeneratingCatalog(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      artist: '',
      duration: '',
      file: null,
      thumbnail: null
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
      
      // Try to get duration
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        const minutes = Math.floor(audio.duration / 60);
        const seconds = Math.floor(audio.duration % 60);
        setFormData(prev => ({ ...prev, duration: `${minutes}:${seconds.toString().padStart(2, '0')}` }));
        URL.revokeObjectURL(audio.src);
      };
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, thumbnail: file }));
    }
  };

  const handleUpload = async () => {
    if (!formData.file || !formData.title || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description || '');
    data.append('category', formData.category);
    data.append('artist', formData.artist || '');
    data.append('duration', formData.duration || '0:00');
    data.append('file', formData.file);
    if (formData.thumbnail) {
      data.append('thumbnail', formData.thumbnail);
    }

    try {
      await axios.post(`${API}/admin/audio`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Audio uploaded successfully!');
      setShowUploadDialog(false);
      resetForm();
      fetchAudio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  // Bulk upload handlers
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return ext.endsWith('.mp3') || ext.endsWith('.wav') || ext.endsWith('.ogg') || 
             ext.endsWith('.m4a') || ext.endsWith('.aac');
    });
    
    if (validFiles.length !== files.length) {
      toast.warning(`${files.length - validFiles.length} file(s) were skipped (unsupported format)`);
    }
    
    setBulkFiles(validFiles);
    setBulkResults(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select audio files to upload');
      return;
    }

    setBulkUploading(true);
    setBulkProgress({ current: 0, total: bulkFiles.length });
    setBulkResults(null);

    const data = new FormData();
    bulkFiles.forEach(file => {
      data.append('files', file);
    });
    data.append('category', bulkCategory);

    try {
      const response = await axios.post(`${API}/admin/audio/bulk`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBulkProgress({ 
            current: Math.round((percentCompleted / 100) * bulkFiles.length), 
            total: bulkFiles.length 
          });
        }
      });

      setBulkResults(response.data);
      
      if (response.data.uploaded_count > 0) {
        toast.success(`Successfully uploaded ${response.data.uploaded_count} audio file(s)!`);
        fetchAudio();
      }
      
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} file(s) failed to upload`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
      setBulkResults({ uploaded: [], uploaded_count: 0, errors: [{ error: 'Upload failed' }], error_count: 1 });
    } finally {
      setBulkUploading(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkFiles([]);
    setBulkCategory('other');
    setBulkResults(null);
    setBulkProgress({ current: 0, total: 0 });
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
  };

  const closeBulkDialog = () => {
    setShowBulkUploadDialog(false);
    resetBulkUpload();
  };

  const handleEdit = (audio) => {
    setSelectedAudio(audio);
    setFormData({
      title: audio.title,
      description: audio.description || '',
      category: audio.category,
      artist: audio.artist || '',
      duration: audio.duration || '',
      file: null,
      thumbnail: null
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!selectedAudio) return;

    setUploading(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description || '');
    data.append('category', formData.category);
    data.append('artist', formData.artist || '');
    data.append('duration', formData.duration || '');
    if (formData.thumbnail) {
      data.append('thumbnail', formData.thumbnail);
    }

    try {
      await axios.put(`${API}/admin/audio/${selectedAudio.audio_id}`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Audio updated successfully!');
      setShowEditDialog(false);
      setSelectedAudio(null);
      resetForm();
      fetchAudio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update audio');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAudio) return;

    try {
      await axios.delete(`${API}/admin/audio/${selectedAudio.audio_id}`, { withCredentials: true });
      toast.success('Audio deleted successfully');
      setShowDeleteDialog(false);
      setSelectedAudio(null);
      fetchAudio();
    } catch (error) {
      toast.error('Failed to delete audio');
    }
  };

  const togglePlay = (audio) => {
    if (playingId === audio.audio_id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = audio.file_url.startsWith('/') 
          ? `${BACKEND_URL}${audio.file_url}` 
          : audio.file_url;
        audioRef.current.play();
        setPlayingId(audio.audio_id);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  return (
    <AdminLayout>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />
      
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
              Audio Management
            </h1>
            <p className="opacity-70 mt-1">
              Upload and manage audio files for the public audio library
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generateCatalog}
              disabled={generatingCatalog}
              variant="outline"
              className="gap-2"
              data-testid="generate-catalog-btn"
            >
              {generatingCatalog ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <FileJson className="w-4 h-4" />
              )}
              {generatingCatalog ? 'Generating...' : 'Generate JSON Catalog'}
            </Button>
            <Button
              onClick={() => { resetBulkUpload(); setShowBulkUploadDialog(true); }}
              variant="outline"
              className="gap-2"
              data-testid="bulk-upload-btn"
            >
              <Files className="w-4 h-4" />
              Bulk Upload
            </Button>
            <Button
              onClick={() => { resetForm(); setShowUploadDialog(true); }}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
              data-testid="add-audio-btn"
            >
              <Plus className="w-4 h-4" />
              Add Audio
            </Button>
          </div>
        </div>

        {/* Catalog URL Display */}
        {catalogUrl && (
          <div 
            className="flex items-center justify-between p-4 rounded-xl"
            style={{ 
              backgroundColor: `${currentAdminTheme.primary}10`,
              border: `1px solid ${currentAdminTheme.primary}30`
            }}
          >
            <div className="flex items-center gap-3">
              <FileJson className="w-5 h-5" style={{ color: currentAdminTheme.primary }} />
              <div>
                <p className="font-medium" style={{ color: currentAdminTheme.foreground }}>JSON Catalog Generated</p>
                <p className="text-sm opacity-60 break-all">{catalogUrl}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(catalogUrl);
                  toast.success('URL copied to clipboard!');
                }}
              >
                Copy URL
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(catalogUrl, '_blank')}
                className="gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                View
              </Button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentAdminTheme.primary}20` }}>
                <Music className="w-5 h-5" style={{ color: currentAdminTheme.primary }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>{audioFiles.length}</p>
                <p className="text-sm opacity-60">Total Files</p>
              </div>
            </div>
          </div>
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentAdminTheme.secondary}20` }}>
                <FolderOpen className="w-5 h-5" style={{ color: currentAdminTheme.secondary }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
                  {new Set(audioFiles.map(a => a.category)).size}
                </p>
                <p className="text-sm opacity-60">Categories</p>
              </div>
            </div>
          </div>
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentAdminTheme.accent}20` }}>
                <BarChart2 className="w-5 h-5" style={{ color: currentAdminTheme.accent }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
                  {audioFiles.reduce((sum, a) => sum + (a.play_count || 0), 0)}
                </p>
                <p className="text-sm opacity-60">Total Plays</p>
              </div>
            </div>
          </div>
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentAdminTheme.primary}20` }}>
                <FileAudio className="w-5 h-5" style={{ color: currentAdminTheme.primary }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
                  {formatFileSize(audioFiles.reduce((sum, a) => sum + (a.file_size || 0), 0))}
                </p>
                <p className="text-sm opacity-60">Total Size</p>
              </div>
            </div>
          </div>
        </div>

        {/* Audio List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : audioFiles.length > 0 ? (
          <div className="space-y-4">
            {audioFiles.map((audio) => (
              <div
                key={audio.audio_id}
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{ 
                  backgroundColor: currentAdminTheme.cardBackground,
                  border: `1px solid ${currentAdminTheme.border}`
                }}
                data-testid={`audio-item-${audio.audio_id}`}
              >
                {/* Thumbnail / Play Button */}
                <button
                  onClick={() => togglePlay(audio)}
                  className="relative w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden group"
                  style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                >
                  {audio.thumbnail_url ? (
                    <img 
                      src={`${BACKEND_URL}${audio.thumbnail_url}`} 
                      alt={audio.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Music className="w-8 h-8" style={{ color: currentAdminTheme.primary }} />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {playingId === audio.audio_id ? (
                      <Pause className="w-6 h-6 text-white" />
                    ) : (
                      <Play className="w-6 h-6 text-white ml-1" />
                    )}
                  </div>
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate" style={{ color: currentAdminTheme.foreground }}>
                    {audio.title}
                  </h3>
                  <div className="flex items-center gap-4 mt-1 text-sm opacity-60">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {audio.artist}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {audio.duration}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: `${currentAdminTheme.secondary}20`, color: currentAdminTheme.secondary }}>
                      {getCategoryLabel(audio.category)}
                    </span>
                  </div>
                  {audio.description && (
                    <p className="text-sm opacity-50 mt-1 truncate">{audio.description}</p>
                  )}
                </div>

                {/* Stats */}
                <div className="hidden md:flex items-center gap-6 text-sm opacity-60">
                  <div className="text-center">
                    <p className="font-semibold" style={{ color: currentAdminTheme.foreground }}>{audio.play_count || 0}</p>
                    <p className="text-xs">Plays</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold" style={{ color: currentAdminTheme.foreground }}>{formatFileSize(audio.file_size)}</p>
                    <p className="text-xs">Size</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(audio)}
                    data-testid={`edit-audio-${audio.audio_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => { setSelectedAudio(audio); setShowDeleteDialog(true); }}
                    data-testid={`delete-audio-${audio.audio_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-xl font-semibold mb-2" style={{ color: currentAdminTheme.foreground }}>
              No audio files yet
            </h3>
            <p className="opacity-60 mb-6">Upload your first audio file to get started</p>
            <Button
              onClick={() => { resetForm(); setShowUploadDialog(true); }}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
            >
              <Upload className="w-4 h-4" />
              Upload Audio
            </Button>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload New Audio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Audio File *</Label>
              <div 
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileAudio className="w-5 h-5" />
                    <span className="font-medium">{formData.file.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm opacity-60">Click to select audio file</p>
                    <p className="text-xs opacity-40 mt-1">MP3, WAV, OGG, M4A, AAC</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="audio-file-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter audio title"
                data-testid="audio-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="audio-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Artist</Label>
                <Input
                  value={formData.artist}
                  onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
                  placeholder="Artist name"
                  data-testid="audio-artist-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the audio content"
                rows={3}
                data-testid="audio-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Thumbnail Image (Optional)</Label>
              <div 
                className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => thumbnailInputRef.current?.click()}
              >
                {formData.thumbnail ? (
                  <div className="flex items-center justify-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    <span>{formData.thumbnail.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm opacity-60">
                    <ImageIcon className="w-5 h-5" />
                    <span>Click to add cover image</span>
                  </div>
                )}
              </div>
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="hidden"
                data-testid="audio-thumbnail-input"
              />
            </div>

            {formData.duration && (
              <p className="text-sm opacity-60">Duration: {formData.duration}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !formData.file || !formData.title || !formData.category}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
              data-testid="upload-audio-submit"
            >
              {uploading ? (
                <>Uploading...</>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUploadDialog} onOpenChange={closeBulkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Files className="w-5 h-5" />
              Bulk Upload Audio Files
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* File Selection */}
            <div className="space-y-2">
              <Label>Select Audio Files *</Label>
              <div 
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => bulkFileInputRef.current?.click()}
              >
                {bulkFiles.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2 text-lg font-medium">
                      <FileAudio className="w-6 h-6" style={{ color: currentAdminTheme.primary }} />
                      <span>{bulkFiles.length} file(s) selected</span>
                    </div>
                    <p className="text-sm opacity-60">
                      Total size: {formatFileSize(bulkFiles.reduce((sum, f) => sum + f.size, 0))}
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="font-medium">Click to select multiple audio files</p>
                    <p className="text-sm opacity-60 mt-1">MP3, WAV, OGG, M4A, AAC</p>
                    <p className="text-xs opacity-40 mt-2">File names will be used as titles (editable after upload)</p>
                  </>
                )}
              </div>
              <input
                ref={bulkFileInputRef}
                type="file"
                accept="audio/*"
                multiple
                onChange={handleBulkFileSelect}
                className="hidden"
                data-testid="bulk-audio-file-input"
              />
            </div>

            {/* Selected Files List */}
            {bulkFiles.length > 0 && !bulkResults && (
              <div className="space-y-2">
                <Label>Selected Files ({bulkFiles.length})</Label>
                <div 
                  className="max-h-48 overflow-y-auto rounded-lg p-3 space-y-2"
                  style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
                >
                  {bulkFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <FileAudio className="w-4 h-4 flex-shrink-0 opacity-60" />
                      <span className="truncate flex-1">{file.name}</span>
                      <span className="opacity-50 text-xs">{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div className="space-y-2">
              <Label>Default Category for All Files</Label>
              <Select 
                value={bulkCategory} 
                onValueChange={setBulkCategory}
              >
                <SelectTrigger data-testid="bulk-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs opacity-50">You can change individual categories after upload</p>
            </div>

            {/* Upload Progress */}
            {bulkUploading && (
              <div className="space-y-2 p-4 rounded-lg" style={{ backgroundColor: `${currentAdminTheme.primary}10` }}>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: currentAdminTheme.primary }} />
                  <span className="font-medium">Uploading files...</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${currentAdminTheme.border}` }}>
                  <div 
                    className="h-full transition-all duration-300"
                    style={{ 
                      width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%`,
                      backgroundColor: currentAdminTheme.primary 
                    }}
                  />
                </div>
                <p className="text-sm opacity-60">{bulkProgress.current} of {bulkProgress.total} files</p>
              </div>
            )}

            {/* Upload Results */}
            {bulkResults && (
              <div className="space-y-3">
                {bulkResults.uploaded_count > 0 && (
                  <div className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-600">
                        {bulkResults.uploaded_count} file(s) uploaded successfully
                      </p>
                      <p className="text-sm opacity-70 mt-1">
                        You can now edit the titles and details from the list below
                      </p>
                    </div>
                  </div>
                )}
                
                {bulkResults.error_count > 0 && (
                  <div className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-600">
                        {bulkResults.error_count} file(s) failed to upload
                      </p>
                      <ul className="text-sm opacity-70 mt-1 space-y-1">
                        {bulkResults.errors.map((err, idx) => (
                          <li key={idx}>{err.filename}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBulkDialog}>
              {bulkResults ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResults && (
              <Button
                onClick={handleBulkUpload}
                disabled={bulkUploading || bulkFiles.length === 0}
                className="gap-2"
                style={{ backgroundColor: currentAdminTheme.primary }}
                data-testid="bulk-upload-submit"
              >
                {bulkUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {bulkFiles.length} File(s)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Audio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter audio title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Artist</Label>
                <Input
                  value={formData.artist}
                  onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
                  placeholder="Artist name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Update Thumbnail (Optional)</Label>
              <div 
                className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => thumbnailInputRef.current?.click()}
              >
                {formData.thumbnail ? (
                  <div className="flex items-center justify-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    <span>{formData.thumbnail.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm opacity-60">
                    <ImageIcon className="w-5 h-5" />
                    <span>Click to change cover image</span>
                  </div>
                )}
              </div>
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="hidden"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={uploading || !formData.title || !formData.category}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
            >
              {uploading ? 'Saving...' : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audio</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAudio?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
